/**
 * Auto-import (Skip Review) hooks — background-job version.
 *
 * Earlier this hook ran the approve loop client-side: fetch every
 * normalization run, POST /:id/approve sequentially. That works fine
 * for the small spreadsheets the feature was originally tuned for
 * (under a hundred rows) but collapses on jobs with thousands of
 * records — the user has to keep the tab visible for hours and any
 * tab close / refresh strands the in-progress import.
 *
 * The new flow is:
 *
 *   1. After parse-and-persist, the upload page calls
 *      `useStartAutoImport()` which POSTs the kickoff endpoint.
 *      The backend enqueues a pg-boss AUTO_APPROVE job and returns
 *      202 immediately.
 *   2. We stash the job id in localStorage so the banner on
 *      `/ingestion` can render it on next mount.
 *   3. The banner uses `useAutoImportStatus(jobId)` to poll the
 *      status endpoint every two seconds. The handler updates the
 *      counters every ~5s/50 records, so two-second polling gives
 *      a smooth-enough bar without hammering the server.
 *   4. When the response shows status === 'completed' (or
 *      completed_with_errors) the polling stops; the banner shows
 *      a final summary and a link into the reviewer or properties
 *      list.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { httpClient } from '../api/http-client.js';

// ============================================================================
// Constants
// ============================================================================

// LocalStorage key the banner uses to find an in-flight job. We
// store the ingestion job id (not the pg-boss job id) because that's
// what the status endpoint takes and what the user navigates by.
export const ACTIVE_AUTO_IMPORT_STORAGE_KEY = 'aig:active-auto-import-job-id';

// Custom event name dispatched whenever the active job id changes
// in this tab. The browser's built-in 'storage' event only fires in
// OTHER tabs (so cross-tab sync works for free) — we need our own
// signal for same-tab listeners (the global Layout toast) to react
// the instant the upload page kicks off a new import without
// requiring a polling loop on every page.
export const ACTIVE_AUTO_IMPORT_EVENT = 'aig:active-auto-import-changed';

// Poll cadence. Two seconds is fast enough that the bar feels live
// against the handler's ~5s/50-record update window, and slow
// enough that idle browser tabs aren't generating 30 req/min.
const POLL_INTERVAL_MS = 2000;

// ============================================================================
// Types — mirror the backend status endpoint shape
// ============================================================================

export type AutoImportStatus =
  | 'idle'
  | 'running'
  | 'completed'
  | 'completed_with_errors';

export interface AutoImportError {
  run_id: string;
  message: string;
}

export interface AutoImportProgress {
  ingestionJobId: string;
  status: AutoImportStatus;
  startedAt: string | null;
  completedAt: string | null;
  total: number;
  completed: number;
  failed: number;
  errors: AutoImportError[];
}

interface StatusResponse {
  success: boolean;
  data: AutoImportProgress;
}

interface StartResponse {
  success: boolean;
  data: { ingestionJobId: string; bgJobId: string | null };
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Kick off the background auto-approve job. Returns when pg-boss
 * has accepted the enqueue — the actual approves run server-side.
 *
 * Side effect: writes the ingestion job id to localStorage so the
 * banner on /ingestion can pick it up after navigation.
 */
export function useStartAutoImport() {
  return useMutation({
    mutationFn: async (jobId: string): Promise<StartResponse['data']> => {
      const res = await httpClient.post<StartResponse>(
        `/normalization/jobs/${jobId}/auto-approve`,
        {}
      );
      try {
        window.localStorage.setItem(ACTIVE_AUTO_IMPORT_STORAGE_KEY, jobId);
        // Notify same-tab listeners (the global Layout toast). The
        // 'storage' event doesn't fire in the originating tab, so
        // without this dispatch the toast wouldn't appear until the
        // user navigated to a route that re-mounts.
        window.dispatchEvent(new Event(ACTIVE_AUTO_IMPORT_EVENT));
      } catch {
        // Private mode / quota — toast just won't render. Not fatal.
      }
      return res.data;
    },
  });
}

/**
 * Poll auto-approve status for a job. Stops polling once the
 * handler has stamped completed_at. Pass `enabled=false` to halt
 * polling without unmounting (used by the banner once the user
 * clicks Dismiss).
 */
export function useAutoImportStatus(
  jobId: string | null,
  options?: { enabled?: boolean }
) {
  const enabled = (options?.enabled ?? true) && Boolean(jobId);

  return useQuery({
    queryKey: ['auto-import-status', jobId],
    enabled,
    queryFn: async (): Promise<AutoImportProgress | null> => {
      if (!jobId) return null;
      const res = await httpClient.get<StatusResponse>(
        `/ingestion/jobs/${jobId}/auto-approve-status`
      );
      return res.data;
    },
    // Stop polling once the job is finished. React Query's
    // refetchInterval can be a function that inspects the latest
    // data — return false to disable subsequent fetches.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return POLL_INTERVAL_MS;
      if (data.status === 'completed' || data.status === 'completed_with_errors') {
        return false;
      }
      return POLL_INTERVAL_MS;
    },
    // Keep refetching even when the tab is backgrounded — the user
    // can leave /ingestion open in another tab and still get an
    // up-to-date count when they switch back.
    refetchIntervalInBackground: true,
    // Status is cheap and cache-busts naturally as the bg job
    // mutates the row; keep stale window short.
    staleTime: 0,
  });
}

/**
 * Clear the localStorage pointer. Called by the toast's Dismiss
 * action and by the kickoff hook when starting a fresh import (so
 * the toast doesn't briefly show stale data from a prior job).
 */
export function clearActiveAutoImport() {
  try {
    window.localStorage.removeItem(ACTIVE_AUTO_IMPORT_STORAGE_KEY);
    window.dispatchEvent(new Event(ACTIVE_AUTO_IMPORT_EVENT));
  } catch {
    // ignore
  }
}

/**
 * Read the active job id from localStorage. Returns null when no
 * import is in flight. Synchronous helper for callers that don't
 * need reactivity (e.g., one-shot reads outside React).
 */
export function readActiveAutoImportJobId(): string | null {
  try {
    return window.localStorage.getItem(ACTIVE_AUTO_IMPORT_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * React hook returning the active auto-import job id (or null) and
 * re-rendering when it changes — whether because this tab's
 * useStartAutoImport just kicked off (custom event), another tab
 * started one (storage event), or the user dismissed it. Used by
 * the global Layout toast so it can appear/disappear without the
 * page being navigated.
 */
export function useActiveAutoImportJobId(): string | null {
  const [jobId, setJobId] = useState<string | null>(() =>
    readActiveAutoImportJobId()
  );

  useEffect(() => {
    const refresh = () => setJobId(readActiveAutoImportJobId());
    // Same-tab change: our custom event from start/clear helpers.
    window.addEventListener(ACTIVE_AUTO_IMPORT_EVENT, refresh);
    // Cross-tab change: native storage event. Filter to our key so
    // an unrelated localStorage write doesn't trigger a re-render.
    const onStorage = (e: StorageEvent) => {
      if (e.key === ACTIVE_AUTO_IMPORT_STORAGE_KEY) refresh();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(ACTIVE_AUTO_IMPORT_EVENT, refresh);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  return jobId;
}
